import { apiRequest, isBackendConfigured } from './api';

export interface LeaderboardEntry {
  alias: string;
  characterId: string;
  stagesCleared: number;
  score: number;
  durationMs: number;
  fullClear: boolean;
  rank: number;
}

export interface LeaderboardPage {
  entries: LeaderboardEntry[];
  page: number;
  pageSize: number;
  total: number;
  totalPages: number;
  maxEntries: number;
}

const pageCache = new Map<number, { expiresAt: number; page: LeaderboardPage }>();
const clientCacheTtlMs = 60_000;

export function clearLeaderboardCache(): void {
  pageCache.clear();
}

export async function loadLeaderboardPage(page = 1): Promise<LeaderboardPage> {
  if (!isBackendConfigured()) return { entries: [], page: 1, pageSize: 50, total: 0, totalPages: 1, maxEntries: 1000 };
  const requestedPage = Number.isFinite(page) ? Math.floor(page) : 1;
  const normalizedPage = Math.max(1, Math.min(20, requestedPage));
  const cached = pageCache.get(normalizedPage);
  if (cached && cached.expiresAt > Date.now()) return cached.page;

  const data = await apiRequest<{ entries?: Array<Record<string, unknown>>; page?: number; pageSize?: number; total?: number; totalPages?: number; maxEntries?: number }>(`/leaderboard?page=${normalizedPage}`);
  const pageData: LeaderboardPage = {
    entries: (data.entries ?? []).map((row) => {
      const stagesCleared = Number(row.stages_cleared ?? 0);
      return {
        alias: String(row.alias),
        characterId: String(row.character_id),
        stagesCleared,
        score: Number(row.score),
        durationMs: Number(row.duration_ms),
        fullClear: stagesCleared >= 10,
        rank: Number(row.global_rank ?? 0),
      };
    }),
    page: Number(data.page ?? normalizedPage),
    pageSize: Number(data.pageSize ?? 50),
    total: Number(data.total ?? 0),
    totalPages: Number(data.totalPages ?? 1),
    maxEntries: Number(data.maxEntries ?? 1000),
  };
  pageCache.set(normalizedPage, { expiresAt: Date.now() + clientCacheTtlMs, page: pageData });
  return pageData;
}
